import { useContext, useState, useEffect, useMemo } from "react";
import { FormattedDate, FormattedTime } from "../../components/common";
import { AppContext } from "../../contextProviders/AppContext";
import Categories from "../../components/Categories";
import ProductCard from "../../components/ProductCard";
import { hideCategories } from "../../features";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import { motion, AnimatePresence } from "framer-motion";
import { loadingMotion } from "../../animation-variants";
import { ADDITIONAL_FIELDS_ALL } from "../../shared/additional-fields";
import { translation } from "../../components/Dictionary";
import React from "react";
import { useId } from "react";
import { Schedule } from "@mui/icons-material";
import { getFixedLanguageSet } from "../../utilities";
import { FilterOffers } from "../../components/FilterOffers";
import { getCurrencyUnit } from "../../shared/money";

export default function Results() {
  const {
    fetchOffersLoading,
    handleActiveStep,
    language,
    mobileViewPort,
    offers,
    reservationReturn,
    reservationStart,
    selectedCompany,
    setOffers,
  } = useContext(AppContext);

  const theme = useTheme();

  const [selectedFilter, setSelectedFilter] = useState(null);
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [sortBy, setSortBy] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(
    "selectAllCategories"
  );

  // For some companies we don't want to show resultTimeSelector
  const FEATURE_HIDE_IF_NOT_TIMEMATCHES = useMemo(() => {
    if (selectedCompany?.group)
      if (["ebikerental"].includes(selectedCompany?.group)) return true;
    return false;
  }, [selectedCompany]);

  const FEATURE_HIDE_CATEGORIES = useMemo(() => {
    if (selectedCompany?.group) return hideCategories(selectedCompany?.group);
  }, [selectedCompany]);

  const sortOptions = useMemo(() => {
    const getTimeDiff = (resolvedTime) => {
      return Math.abs(resolvedTime.getTime() - reservationStart.getTime());
    };
    const getOrderNum = (reservation) => {
      return !!reservation.vehicle.orderNumber
        ? reservation.vehicle.orderNumber
        : 999999;
    };

    return [
      {
        label: translation.get(language, "Sort options top results"),
        avatar: <Schedule style={{ borderRadius: "50%" }} />,
        sort: (a, b) => {
          if (
            getTimeDiff(a.startTime) !== 0 ||
            getTimeDiff(b.startTime) !== 0
          ) {
            return getTimeDiff(a.startTime) - getTimeDiff(b.startTime);
          }
          return getOrderNum(a) - getOrderNum(b);
        },
      },
      {
        label: translation.get(language, "Sort options cheapest results"),
        avatar: <Avatar>{getCurrencyUnit()}</Avatar>,
        sort: (a, b) => a.vehicleOfferPrice - b.vehicleOfferPrice,
      },
      {
        label: translation.get(language, "Sort options most expensive results"),
        avatar: <Avatar>{getCurrencyUnit()}</Avatar>,
        sort: (a, b) => b.vehicleOfferPrice - a.vehicleOfferPrice,
      },
    ];
  }, [language, reservationStart]);

  // Clear offers and selected filters when changing company
  useEffect(() => {
    setSelectedFilter(null);
    setOffers([]);
    setSelectedCategory("selectAllCategories");
  }, [selectedCompany]);

  const availableOffers = useMemo(() => {
    // .sort(sortOptions[sortBy].sort)
    if (fetchOffersLoading) {
      return [];
    }

    const offersByCategory =
      !!selectedCategory && selectedCategory !== "selectAllCategories"
        ? offers.filter((offer) => {
            return offer?.vehicle?.category?.id === selectedCategory;
          })
        : offers;
    if (!selectedFilter) {
      return Array.isArray(offersByCategory)
        ? offersByCategory.sort(
            sortOptions[sortBy]?.sort ? sortOptions[sortBy].sort : () => 0
          )
        : [];
    }

    const filteredOffers = offersByCategory.reduce((acc, offer) => {
      const hasAllFilters = Object.entries(selectedFilter).every(
        ([key, value]) =>
          offer.vehicle.additionalFields.some(
            (field) => field.key === key && field.value === value
          )
      );
      if (hasAllFilters) {
        acc.push(offer);
      }
      return acc;
    }, []);
    return filteredOffers.sort(
      sortOptions[sortBy]?.sort ? sortOptions[sortBy].sort : () => 0
    );
  }, [
    selectedCategory,
    selectedFilter,
    fetchOffersLoading,
    offers,
    sortBy,
    sortOptions,
  ]);

  if (offers === "error") {
    return <EmptyState>Jokin meni vikaan</EmptyState>;
  }
  /*   if (dateHasError) {
    return <EmptyState>Tarkista nouto- ja palautusaika </EmptyState>;
  } */

  // incase no selected company, return to step 1
  if (!selectedCompany) {
    return handleActiveStep(0);
  }

  if (offers === "no offers") {
    return (
      <Box
        sx={{
          maxWidth: "inherit",
          bgcolor: theme.palette.primary.backgroundColor,
        }}
      >
        <Typography align="center">
          {translation.get(language, "No free times")}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        maxWidth: "inherit",
        bgcolor: theme.palette.primary.backgroundColor,
      }}
    >
      {fetchOffersLoading && <LoadingScreen />}
      {!FEATURE_HIDE_CATEGORIES && !fetchOffersLoading && offers.length > 0 && (
        <motion.div
          initial="initial"
          animate="enter"
          exit="exit"
          variants={loadingMotion}
          key="categories-container"
          style={{
            //maxWidth: "inherit",
            display: "flex",
            width: "100%",
            //minWidth: "100%",
            justifyContent: "center",
            alignItems: "center",
            marginTop: "45px",
          }}
        >
          <Categories
            offers={offers}
            selectedCategory={selectedCategory}
            setSelectedCategory={setSelectedCategory}
            selectedCompany={selectedCompany}
            mobileViewPort={mobileViewPort}
            theme={theme}
          />
        </motion.div>
      )}
      <FilterOffers
        offers={availableOffers}
        fetchOffersLoading={fetchOffersLoading}
        setSortBy={setSortBy}
        sortBy={sortBy}
        sortOptions={sortOptions}
        setSelectedFilter={setSelectedFilter}
      />
      <SelectedFilters
        selectedFilter={selectedFilter}
        setSelectedFilter={setSelectedFilter}
      />
      {!fetchOffersLoading && (
        <ListOffers
          availableOffers={availableOffers}
          FEATURE_HIDE_IF_NOT_TIMEMATCHES={FEATURE_HIDE_IF_NOT_TIMEMATCHES}
          fetchOffersLoading={fetchOffersLoading}
          language={language}
          mobileViewPort={mobileViewPort}
          reservationReturn={reservationReturn}
          reservationStart={reservationStart}
          selectedCategory={selectedCategory}
          sortBy={sortBy}
          setShowAllProducts={setShowAllProducts}
          showAllProducts={showAllProducts}
          sortOptions={sortOptions}
        />
      )}
    </Box>
  );
}

function SelectedFilters({ selectedFilter, setSelectedFilter }) {
  const { language } = useContext(AppContext);
  const fixedLanguage = getFixedLanguageSet(language);

  if (!selectedFilter) return null;
  return (
    <Stack direction={"row"} spacing={2}>
      {Object.entries(selectedFilter).map(([key, value]) => {
        const attributeData = ADDITIONAL_FIELDS_ALL.find((a) => a.key === key);
        const translatedValue =
          attributeData?.optionsT?.[fixedLanguage]?.[value];

        return (
          <Chip
            label={`${attributeData?.labelT[fixedLanguage]}: ${
              translatedValue ?? value
            } `}
            key={`selected-filter-${key}-x`}
            onDelete={() => {
              const activeFilters = Object.entries(selectedFilter).reduce(
                (acc, [key, value]) => {
                  if (key === attributeData.key) {
                    return acc;
                  }
                  return { ...acc, [key]: value };
                },
                {}
              );

              setSelectedFilter(
                Object.entries(activeFilters)?.length > 0 ? activeFilters : null
              );
            }}
          />
        );
      })}
    </Stack>
  );
}
const LoadingScreen = () => {
  return (
    <motion.div
      variants={loadingMotion}
      initial="hidden"
      animate="show"
      exit={{ opacity: 0 }}
      key="loadingScreen"
      style={{
        maxWidth: "inherit",
        display: "flex",
        width: "100%",
        justifyContent: "center",
        alignItems: "center",
        height: "100%",
      }}
    >
      <div
        style={{
          paddingTop: 50,
          paddingBottom: 150,
        }}
      >
        <CircularProgress size={100} />
      </div>
    </motion.div>
  );
};

function ListOffers({
  activeStep,
  availableOffers,
  FEATURE_HIDE_IF_NOT_TIMEMATCHES,
  fetchOffersLoading,
  selectedCategory,
  sortBy,
  setShowAllProducts,
  showAllProducts,
  sortOptions,
}) {
  const id = useId();
  const { mobileViewPort } = useContext(AppContext);
  if (availableOffers === "no offers") {
    return (
      <NoFreeTimes
        FEATURE_HIDE_IF_NOT_TIMEMATCHES={FEATURE_HIDE_IF_NOT_TIMEMATCHES}
      />
    );
  } else {
    return (
      <Box
        sx={{
          // These control the width of the ProductCards
          paddingLeft: !mobileViewPort ? "5%" : 0,
          paddingRight: !mobileViewPort ? "5%" : 0,
          // Add this to the container, might ease out the transition between categories
          minHeight: "785px",
        }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={`${id}-resultsContainer`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              staggerChildren: 0.2,
              delayChildren: 0.2,
            }}
            sx={{
              display: "flex",
              flexDirection: "column",
            }}
          >
            {availableOffers
              .slice(0, showAllProducts ? availableOffers.length : 5)
              .map((offer, index) => {
                return (
                  <motion.div
                    layout
                    key={`${offer.vehicle.id}-${selectedCategory}-${index}`}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    //  transition={{ duration: 0.1 }}
                    style={{
                      maxWidth: "inherit",
                      display: "flex",
                      /*   justifyContent: "center",
                    alignItems: "center", */
                    }}
                  >
                    <ProductCard
                      key={offer.vehicle.id}
                      initialOffer={offer}
                      activeStep={activeStep}
                      FEATURE_HIDE_IF_NOT_TIMEMATCHES={
                        FEATURE_HIDE_IF_NOT_TIMEMATCHES
                      }
                    />
                  </motion.div>
                );
              })}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              //   transition={{ duration: 5 }}
              key={`${id}+moreItemsButton`}
              style={{
                display: "flex",
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
                marginTop: "10px",
              }}
            >
              {!showAllProducts &&
                availableOffers.length > 5 &&
                !fetchOffersLoading && (
                  <ShowMoreProductsButton onClick={setShowAllProducts} />
                )}
            </motion.div>
          </motion.div>
        </AnimatePresence>
      </Box>
    );
  }
}

const timeFlexibility = 48 * 60 * 60 * 1000; //48h
function NoFreeTimes({ FEATURE_HIDE_IF_NOT_TIMEMATCHES }) {
  const {
    reservationStart,
    reservationReturn,
    language,
    FEATURE_FIXED_RESERVATION_DURATION,
    selectedCompany,
  } = useContext(AppContext);
  const minDate = new Date(reservationStart.getTime() - timeFlexibility);
  const maxDate = new Date(reservationStart.getTime() + timeFlexibility);

  const FEATURE_HUUMA_NO_FREETIMES_LABEL = () => {
    return (
      <Box>
        <Box>Ei sähköisesti varattavissa kyseiselle ajankohdalle.</Box>
        <Box>
          Tiedustele ajankohtaa sähköpostitse huuma@tuolihuput.fi osoitteesta.
        </Box>
      </Box>
    );
  };

  if (FEATURE_FIXED_RESERVATION_DURATION) {
    return (
      <EmptyState>
        {selectedCompany?.group === "huuma" &&
          FEATURE_HUUMA_NO_FREETIMES_LABEL()}
        <Typography variant="subtitle2" component="span">
          <NoWrap>
            (<FormattedDate date={reservationStart} />{" "}
            <FormattedTime date={reservationStart} />
          </NoWrap>
          {" - "}
          <NoWrap>
            <FormattedDate date={reservationReturn} />{" "}
            <FormattedTime date={reservationReturn} />)
          </NoWrap>
        </Typography>
      </EmptyState>
    );
  }

  if (FEATURE_HIDE_IF_NOT_TIMEMATCHES) {
    return (
      <EmptyState>
        {translation.get(language, "No free times")}
        <Typography variant="subtitle2" component="span">
          <NoWrap>
            (<FormattedDate date={reservationStart} />{" "}
            <FormattedTime date={reservationStart} />
          </NoWrap>
          {" - "}
          <NoWrap>
            <FormattedDate date={reservationReturn} />{" "}
            <FormattedTime date={reservationReturn} />)
          </NoWrap>
        </Typography>
      </EmptyState>
    );
  }
  return (
    <EmptyState>
      {translation.get(language, "No available products")}
      <Typography variant="subtitle2" component="span">
        <NoWrap>
          (<FormattedDate date={minDate} /> <FormattedTime date={minDate} />
        </NoWrap>
        {" - "}

        <NoWrap>
          <FormattedDate date={maxDate} /> <FormattedTime date={maxDate} />)
        </NoWrap>
      </Typography>
    </EmptyState>
  );
}

// Control the amount of products shown
const ShowMoreProductsButton = ({ onClick }) => {
  const theme = useTheme();
  const { language } = useContext(AppContext);
  return (
    <Button
      /*   className={classes.moreProducts} */
      fullWidth
      onClick={() => onClick(true)}
      sx={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.buttonText,
        padding: 2,
        borderRadius: 5,
        marginBottom: 10,
      }}
    >
      <Typography
        align="center"
        style={{
          textTransform: "none",
          letterSpacing: 1,
          fontFamily: "Sofia Pro",
        }}
      >
        {translation.get(language, "More results button text")}
      </Typography>
    </Button>
  );
};

function NoWrap(props) {
  return <span style={{ whiteSpace: "nowrap" }} {...props} />;
}

function EmptyState({ children }) {
  const theme = useTheme();
  return (
    <Grid
      item
      container
      justifyContent="center"
      direction="column"
      alignItems="center"
      style={{ textAlign: "center", color: theme.palette.primary.maintText }}
    >
      <Typography
        variant="h5"
        style={{
          alignSelf: "center",
          marginTop: "25px",
          color: theme.palette.primary.maintText,
        }}
      >
        {children}
      </Typography>
    </Grid>
  );
}
